import { Injectable } from '@angular/core';
import * as THREE from '@phdsoft/three';

/**
 * Canonical shape types for inspection point InstancedMesh batches.
 * These match the visual types rendered by the legacy PointsRender service.
 */
export enum PointShapeType {
  Plate = 'Plate',
  Web = 'Web',
  Flange = 'Flange',
  FacePlate = 'FacePlate',
  BeamPart = 'BeamPart',
  Cable = 'Cable',
  Pipe = 'Pipe',
}

/**
 * Eagerly builds and caches all 7 canonical BufferGeometry shapes for
 * inspection point rendering. Geometries are shared across all InstancedMesh
 * batches (userData.shared=true) and must NOT be disposed during section
 * teardown traversal.
 *
 * Injectable as a root-level singleton — instantiated once per app lifetime.
 */
@Injectable({ providedIn: 'root' })
export class PointShapeGeometryCache {
  private _geometries: Map<PointShapeType, THREE.BufferGeometry> = new Map();

  constructor() {
    this._buildAll();
  }

  /**
   * Return the cached BufferGeometry for the given shape type.
   * Always returns the same object instance for the same type (identity
   * guarantee — callers must NOT dispose the returned geometry).
   * @throws Error if the shape type is unknown or the cache has been disposed.
   */
  get(shapeType: PointShapeType): THREE.BufferGeometry {
    const geo = this._geometries.get(shapeType);
    if (geo === undefined) {
      throw new Error(
        `PointShapeGeometryCache: unknown or disposed shape type "${shapeType}". ` +
          `Valid types: ${Object.values(PointShapeType).join(', ')}`,
      );
    }
    return geo;
  }

  /**
   * Dispose all cached geometries and clear the map.
   * After calling this, all get() calls will throw.
   */
  dispose(): void {
    for (const geo of this._geometries.values()) {
      geo.dispose();
    }
    this._geometries.clear();
  }

  // ---------------------------------------------------------------------------
  // Private build helpers
  // ---------------------------------------------------------------------------

  private _buildAll(): void {
    // Flat shapes — use ShapeGeometry (front-face only, indexed)
    // This produces fewer triangles than ExtrudeGeometry({depth:0}) which also
    // generates back-face and side cap triangles (matching legacy face/2 loop).
    this._geometries.set(PointShapeType.Plate, this._buildFlatShape(this._plateShape(), 'Plate'));
    this._geometries.set(PointShapeType.Web, this._buildFlatShape(this._webShape(), 'Web'));
    this._geometries.set(PointShapeType.Flange, this._buildFlatShape(this._flangeShape(), 'Flange'));
    this._geometries.set(PointShapeType.FacePlate, this._buildFlatShape(this._facePlateShape(), 'FacePlate'));
    this._geometries.set(PointShapeType.Pipe, this._buildFlatShape(this._plateShape(), 'Pipe'));

    // Extruded shapes (depth:1) — use ExtrudeGeometry, all faces
    this._geometries.set(PointShapeType.BeamPart, this._buildExtrudedShape(this._plateShape(), 'BeamPart'));
    this._geometries.set(PointShapeType.Cable, this._buildExtrudedShape(this._plateShape(), 'Cable'));
  }

  /**
   * Build a flat (front-face only) shape geometry using ShapeGeometry.
   * ShapeGeometry produces only the front-face triangles — equivalent to the
   * legacy `faces.length / 2` loop in PointsRender that discarded back-face
   * triangles from depth:0 extrusions. Always produces fewer triangles than a
   * raw ExtrudeGeometry({depth:0}).
   */
  private _buildFlatShape(shape: THREE.Shape, name: string): THREE.BufferGeometry {
    const geo = new THREE.ShapeGeometry(shape);
    geo.computeBoundingBox();
    geo.computeVertexNormals();
    geo.name = name;
    geo.userData['shared'] = true;
    return geo;
  }

  /**
   * Build an extruded (depth:1) shape geometry using ExtrudeGeometry, all
   * faces included (front face + sides + back face).
   */
  private _buildExtrudedShape(shape: THREE.Shape, name: string): THREE.BufferGeometry {
    const geo = new THREE.ExtrudeGeometry(shape, { depth: 1, bevelEnabled: false });
    geo.computeBoundingBox();
    geo.computeVertexNormals();
    geo.name = name;
    geo.userData['shared'] = true;
    return geo;
  }

  // ---------------------------------------------------------------------------
  // Shape definitions (coordinates from PointsRender)
  // ---------------------------------------------------------------------------

  /** Triangle arrow — used for Plate, BeamPart, Cable, Pipe */
  private _plateShape(): THREE.Shape {
    return new THREE.Shape()
      .moveTo(-1, -2)
      .lineTo(-1, 2)
      .lineTo(2, 0)
      .lineTo(-1, -2);
  }

  /** Cross / H-beam shape — used for Web */
  private _webShape(): THREE.Shape {
    return new THREE.Shape()
      .moveTo(-0.5, -3.5)
      .lineTo(-0.5, -2)
      .lineTo(-2, -2)
      .lineTo(-2, 2)
      .lineTo(-0.5, 2)
      .lineTo(-0.5, 3.5)
      .lineTo(0.5, 3.5)
      .lineTo(0.5, 2)
      .lineTo(2, 2)
      .lineTo(2, -2)
      .lineTo(0.5, -2)
      .lineTo(0.5, -3.5)
      .lineTo(-0.5, -3.5);
  }

  /** T-shape — used for Flange */
  private _flangeShape(): THREE.Shape {
    return new THREE.Shape()
      .moveTo(-2, -2)
      .lineTo(-2, 1)
      .lineTo(-3.5, 1)
      .lineTo(-3.5, 2)
      .lineTo(3.5, 2)
      .lineTo(3.5, 1)
      .lineTo(2, 1)
      .lineTo(2, -2)
      .lineTo(-2, -2);
  }

  /** Square — used for FacePlate */
  private _facePlateShape(): THREE.Shape {
    return new THREE.Shape()
      .moveTo(-2, -2)
      .lineTo(-2, 2)
      .lineTo(2, 2)
      .lineTo(2, -2)
      .lineTo(-2, -2);
  }
}
